import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Table, Button, Modal, Form, Input, Switch, Tag, Space, message, Empty, Tooltip, Popconfirm } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { financeSettingsApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../SkeletonLoaders';

const { TextArea } = Input;

const PaymentGatewayTab = forwardRef(({ data, onUpdate }, ref) => {
  const [form] = Form.useForm();
  const [methods, setMethods] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setMethods(data.offline_methods || []);
    }
  }, [data]);

  const handleAdd = () => {
    setEditingMethod(null);
    form.resetFields();
    form.setFieldsValue({ status: true });
    setModalVisible(true);
  };

  useImperativeHandle(ref, () => ({
    openAddModal: handleAdd,
  }));

  const handleEdit = async (record) => {
    setDetailLoading(true);
    setEditingMethod(record);
    setModalVisible(true);
    try {
      const res = await financeSettingsApi.getOfflineMethod(record.id);
      const method = res?.data?.method || res?.method || record;
      form.setFieldsValue({
        name: method.name,
        description: method.description,
        status: method.status === 'yes',
      });
    } catch (error) {
      console.error('Error fetching method details:', error);
      form.setFieldsValue({
        name: record.name,
        description: record.description,
        status: record.status === 'yes',
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await financeSettingsApi.deleteOfflineMethod(id);
      message.success('روش پرداخت با موفقیت حذف شد');
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting method:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در حذف روش پرداخت');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        name: values.name,
        description: values.description || '',
      };

      if (editingMethod) {
        payload.status = values.status ? 'yes' : 'no';
        await financeSettingsApi.updateOfflineMethod(editingMethod.id, payload);
        message.success('روش پرداخت با موفقیت ویرایش شد');
      } else {
        await financeSettingsApi.createOfflineMethod(payload);
        message.success('روش پرداخت جدید با موفقیت ایجاد شد');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingMethod(null);
      onUpdate?.();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error saving method:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error('خطا در ذخیره روش پرداخت');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'نام',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <div className="flex items-center gap-2">
          <CreditCardOutlined className="text-blue-500" />
          <span className="font-medium">{text}</span>
        </div>
      ),
    },
    {
      title: 'توضیحات',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => (
        <span className="text-gray-500">{text || '-'}</span>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (status) =>
        status === 'yes' ? (
          <Tag icon={<CheckCircleOutlined />} color="success">فعال</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">غیرفعال</Tag>
        ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="ویرایش">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              className="text-blue-500 hover:text-blue-600"
            />
          </Tooltip>
          <Popconfirm
            title="حذف روش پرداخت"
            description="آیا از حذف این روش پرداخت اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله، حذف کن"
            cancelText="خیر"
            okButtonProps={{ danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } }}
            cancelButtonProps={{ style: { borderColor: '#d9d9d9', color: '#595959' } }}
          >
            <Tooltip title="حذف">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                className="text-red-500 hover:text-red-600"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <Table
        columns={columns}
        dataSource={methods}
        rowKey="id"
        pagination={false}
        scroll={{ x: 900 }}
        locale={{
          emptyText: (
            <Empty
              description={<span style={{ fontFamily: 'CustomFont' }}>هیچ روش پرداختی ثبت نشده</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        style={{ fontFamily: 'CustomFont' }}
      />

      <Modal
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingMethod(null);
          form.resetFields();
        }}
        title={
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <CreditCardOutlined className="text-blue-500" />
            <span>{editingMethod ? 'ویرایش درگاه پرداخت' : 'افزودن درگاه جدید'}</span>
          </div>
        }
        footer={null}
        centered
        width={500}
        destroyOnClose
      >
        {detailLoading ? (
          <TableSkeleton rows={3} columns={1} />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ fontFamily: 'CustomFont' }}
          >
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>نام روش پرداخت</span>}
              name="name"
              rules={[{ required: true, message: 'نام روش پرداخت الزامی است' }]}
            >
              <Input
                placeholder="مثلا: نقدی، کارت به کارت"
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}
              name="description"
            >
              <TextArea
                rows={3}
                placeholder="توضیحات مربوط به این روش پرداخت..."
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>

            {editingMethod && (
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>}
                name="status"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="فعال"
                  unCheckedChildren="غیرفعال"
                />
              </Form.Item>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingMethod(null);
                  form.resetFields();
                }}
                style={{ fontFamily: 'CustomFont', borderColor: '#d9d9d9', color: '#595959' }}
              >
                انصراف
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="bg-green-600 hover:bg-green-700 border-0"
                style={{ fontFamily: 'CustomFont' }}
              >
                {editingMethod ? 'ذخیره تغییرات' : 'ایجاد'}
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
});

export default PaymentGatewayTab;
